// The shared UNDATED TRAY (design brief §5.2): the honest home for items a time-shaped axis cannot
// place — a null dateField on the calendar, a null dateRange on the timeline. Both views import it so
// an undated item is NEVER dropped or invented onto a day it does not own (P-SELF). Each entry is the
// same clickable chip the day cells and lanes use — subject + the needs-a-decision badge, a button
// firing onOpenCard (the BoardItemCard aria-label idiom: `Open {subjectDisplay}`).

import type { WorkItemRow } from '../../projections.ts';
import { WorkMiniBadge } from '../../work-ui.tsx';

/** One time-axis chip — a signal, never a control (the card page rules). Reused by tray, day cells
 *  and lanes so every clickable item on the time axes carries the same accessible name. */
export function ViewChip({
  item,
  onOpen,
}: {
  readonly item: WorkItemRow;
  readonly onOpen: (itemId: string) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-chip={item.id}
      data-item={item.id}
      data-item-state={item.state}
      onClick={() => onOpen(item.id)}
      aria-label={`Open ${item.subjectDisplay}`}
      className="flex w-full items-center gap-1.5 rounded-sm border border-border bg-surface px-2 py-1 text-left text-xs text-text transition-colors hover:border-border-strong"
    >
      <span className="truncate">{item.subjectDisplay}</span>
      {item.needsDecision ? (
        <span className="ml-auto shrink-0">
          <WorkMiniBadge kind="decide">needs a decision</WorkMiniBadge>
        </span>
      ) : null}
    </button>
  );
}

export interface UndatedTrayProps {
  readonly items: readonly WorkItemRow[];
  readonly onOpenCard: (itemId: string) => void;
}

/** The labelled tray of items the axis cannot place — absent when everything is dated. */
export function UndatedTray({ items, onOpenCard }: UndatedTrayProps): React.JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <section
      aria-label={`Undated — ${String(items.length)} not on the calendar`}
      data-undated-tray={items.length}
      className="mt-4 rounded-md border border-dashed border-border bg-surface-sunken p-3"
    >
      <p className="mb-2 ritona-numeric text-xs uppercase tracking-eyebrow text-text-muted">
        Undated
      </p>
      <div className="flex flex-col gap-1.5">
        {items.map((item) => (
          <ViewChip key={item.id} item={item} onOpen={onOpenCard} />
        ))}
      </div>
    </section>
  );
}
